/**
 *
 * OrdersPage
 *
 */

import { Modal } from 'antd';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import LoadingIndicator from 'components/LoadingIndicator';
import OrderTable from 'components/OrderTable/OrderTable';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import {
  selectCountPendingOrder,
  selectPendingApprovalMode,
  selectUserPermissions,
} from 'containers/MainLayout/selectors';
import React, { Fragment, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import translations from 'translations';
import { FulfillmentNote, MutationSubmitFeedbackArgs } from 'types/schema';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import utilsMessages from 'utils/messages';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import InfoIcon from '../../components/Order/Info.svg';
import {
  applyFilter,
  approveOrder as approveOrderActions,
  approveOrders,
  bulkReOrderSubmit,
  clearFilter,
  closeModal,
  declineOrders,
  disapproveOrder as disapproveOrderActions,
  exportCSV,
  fetchTabCounts,
  loadMoreOrders,
  openModal,
  reOrderSubmit,
  submitFeedback,
} from './actions';
import ArrowRight from './ArrowRight.svg';
import Filter from './Filter';
import FilterStatus, { getStatusesByTabKey } from './FilterStatus';
import messages from './messages';
import NoOrder from './NoOrder.svg';
import reducer from './reducer';
import saga from './saga';
import selectOrdersPage, { selectIsOpenModal, selectNote, selectTabCounts } from './selectors';
import { RootState } from './types';

const RatingModal = lazy(() => import('components/RatingModal'));

const FilterResultSection = styled.div`
  background-color: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 2px 8px;
`;

export const Icon = styled(SVGIcon)`
  width: 20px;
  height: 20px;
`;

const OrderApproval = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  background-color: #fffbe6;
  margin: 4px 0 12px;
  justify-content: space-between;
  cursor: pointer;
  border: 1px solid #f8e6a0;
  gap: 8px;
`;

const PendingOrders = styled.span`
  font-weight: bold;
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 0;
  background-color: white;
`;

const SelectActionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

interface OwnProps {
  orderspage: any;
  permissions: string[];
  countPendingOrder: number;
  isPenddingApproval: boolean;
  isOpenModal: boolean;
  note: FulfillmentNote;
  tabCounts: { pending: number | null; waiting: number | null };
}

interface StateProps {}

interface DispatchProps {
  onApplyFilter: (data: any) => void;
  onClearFilter: () => void;
  onExportCSV: (data: any) => void;
  onApproveOrder: (value: any) => void;
  onDisapproveOrder: (value: any) => void;
  onReOrderAll: (value: any) => void;
  onBulkReOrder: (value: any) => void;
  onOpenModal: (value: FulfillmentNote) => void;
  onCloseModal: () => void;
  onSubmitFeedback: (value: MutationSubmitFeedbackArgs['input']) => void;
  onLoadMoreOrders: () => void;
  onFetchTabCounts: () => void;
  onApproveOrders: (orderIds: string[]) => void;
  onDeclineOrders: (orderIds: string[]) => void;
}

type Props = StateProps & DispatchProps & OwnProps;

export class OrdersPage extends React.PureComponent<Props> {
  state = {
    selectedOrderIds: [] as string[],
  };

  public componentDidMount() {
    sendGA4Event(EventName.VIEW_ORDER_HISTORY_PAGE);
    // Fetch tab counts khi component mount
    this.props.onFetchTabCounts();
  }

  public componentDidUpdate(prevProps: Props) {
    if (prevProps.orderspage.search.filter.statuses !== this.props.orderspage.search.filter.statuses) {
      this.setState({ selectedOrderIds: [] });
    }
  }

  public loadMoreOrders = () => {
    this.props.onLoadMoreOrders();
  };

  public onChangeTab = (activeKey: string) => {
    const { search } = this.props.orderspage;
    const statuses = getStatusesByTabKey(activeKey);

    this.props.onApplyFilter({
      filter: {
        ...search.filter,
        statuses: statuses,
      },
    });
  };

  public filterPendingApproval = () => {
    const { search } = this.props.orderspage;

    this.props.onApplyFilter({
      filter: {
        ...search.filter,
        statuses: ['PENDING_APPROVAL'],
        orderDateRange: null,
        deliveryDateRange: null,
      },
    });
  };

  public handleSelectAllOrders = (e: any) => {
    const { orders } = this.props.orderspage;
    const checked = e.target.checked;

    if (checked) {
      const orderIds = orders.data.filter((order: any) => order.__typename === 'Order').map((order: any) => order.id);
      this.setState({ selectedOrderIds: orderIds });
    } else {
      this.setState({ selectedOrderIds: [] });
    }
  };

  public handleSelectOrder = (orderId: string, checked: boolean) => {
    this.setState((prevState: any) => {
      if (checked) {
        return { selectedOrderIds: [...prevState.selectedOrderIds, orderId] };
      }
      return { selectedOrderIds: prevState.selectedOrderIds.filter((id: string) => id !== orderId) };
    });
  };

  public handleApproveOrders = () => {
    const { selectedOrderIds } = this.state;
    if (selectedOrderIds.length) {
      this.props.onApproveOrders(selectedOrderIds);
      this.setState({ selectedOrderIds: [] });
    }
  };

  public handleDeclineOrders = () => {
    const { selectedOrderIds } = this.state;
    if (selectedOrderIds.length) {
      Modal.confirm({
        autoFocusButton: null,
        okButtonProps: {
          danger: true,
        },
        title: translations(messages.confirmDeclineOrders),
        okText: translations(utilsMessages.yes),
        cancelText: translations(utilsMessages.no),
        onOk: () => {
          this.props.onDeclineOrders(selectedOrderIds);
          this.setState({ selectedOrderIds: [] });
        },
      });
    }
  };

  public render() {
    const { orders, search } = this.props.orderspage;
    const permissions = this.props.permissions;
    const { countPendingOrder, isPenddingApproval, isOpenModal, onCloseModal, onOpenModal, note, onSubmitFeedback } =
      this.props;
    const { selectedOrderIds } = this.state;

    const selectableOrders = orders.data.filter((order: any) => order.__typename === 'Order');
    const selectableOrdersCount = selectableOrders.length;

    return (
      <Fragment>
        <Helmet>
          <title>{`${translations(messages.header)} | Nhà Cung Cấp Kamereo`}</title>
          <meta name="description" content={`${translations(messages.header)}`} />
        </Helmet>
        <FilterStatus
          statuses={search.filter.statuses}
          onChangeTab={this.onChangeTab}
          tabCounts={this.props.tabCounts}
        />

        <FilterResultSection>
          <Filter
            statuses={search.filter.statuses}
            supplierName={search.filter.supplierName}
            orderDateStart={search.filter.orderDateRange && search.filter.orderDateRange.start}
            orderDateEnd={search.filter.orderDateRange && search.filter.orderDateRange.end}
            deliveryDateStart={search.filter.deliveryDateRange && search.filter.deliveryDateRange.start}
            deliveryDateEnd={search.filter.deliveryDateRange && search.filter.deliveryDateRange.end}
            onApplyFilter={this.props.onApplyFilter}
            onClearFilter={this.props.onClearFilter}
            onExportCSV={this.props.onExportCSV}
            isPenddingApproval={isPenddingApproval}
            orders={orders}
          />

          {search.filter.statuses === 'PENDING_APPROVAL' ? (
            <SelectWrapper>
              <Checkbox
                checked={selectedOrderIds.length === selectableOrdersCount && selectableOrdersCount > 0}
                indeterminate={!!selectedOrderIds.length && selectedOrderIds.length < selectableOrdersCount}
                onChange={this.handleSelectAllOrders}
              >
                <FormattedMessage {...utilsMessages.selectAll} />
              </Checkbox>

              <SelectActionWrapper>
                <Button onClick={this.handleDeclineOrders} disabled={!selectedOrderIds.length}>
                  <FormattedMessage {...messages.decline} />
                </Button>
                <Button type="primary" onClick={this.handleApproveOrders} disabled={!selectedOrderIds.length}>
                  <FormattedMessage {...utilsMessages.approve} />
                </Button>
              </SelectActionWrapper>
            </SelectWrapper>
          ) : null}

          {isPenddingApproval && countPendingOrder > 0 && !search.filter.statuses && (
            <OrderApproval onClick={() => this.filterPendingApproval()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                <Icon src={InfoIcon} />
                <FormattedMessage {...messages.youHave} />
                <PendingOrders>
                  <FormattedMessage
                    {...messages.pendingOrder}
                    values={{
                      count: countPendingOrder,
                    }}
                  />
                </PendingOrders>
                <FormattedMessage {...messages.pendingOrderText} />
              </div>
              <SVGIcon style={{ minWidth: '16px' }} src={ArrowRight} />
            </OrderApproval>
          )}

          {orders.data.length ? (
            <Fragment>
              <OrderTable
                orders={orders.data}
                permissions={permissions}
                onApproveOrder={this.props.onApproveOrder}
                onDisapproveOrder={this.props.onDisapproveOrder}
                onReOrderAll={this.props.onReOrderAll}
                onBulkReOrder={this.props.onBulkReOrder}
                onOpenModal={onOpenModal}
                hasMore={orders.hasMore}
                loading={orders.loading}
                onLoadMore={this.loadMoreOrders}
                statuses={search.filter.statuses}
                selectedOrderIds={selectedOrderIds}
                onSelectOrder={this.handleSelectOrder}
              />
            </Fragment>
          ) : (
            <p style={{ padding: '40pt', textAlign: 'center' }}>
              <SVGIcon src={NoOrder} />
              <FormattedMessage {...messages.hasNoOrders} />
            </p>
          )}
        </FilterResultSection>

        <Suspense fallback={<LoadingIndicator />}>
          <RatingModal isOpen={isOpenModal} onClose={onCloseModal} note={note} onSubmitFeedback={onSubmitFeedback} />
        </Suspense>
      </Fragment>
    );
  }
}

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  orderspage: selectOrdersPage(),
  permissions: selectUserPermissions(),
  countPendingOrder: selectCountPendingOrder(),
  isPenddingApproval: selectPendingApprovalMode(),
  isOpenModal: selectIsOpenModal(),
  note: selectNote(),
  tabCounts: selectTabCounts(),
});

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    onApplyFilter: (data: any) => dispatch(applyFilter(data)),
    onClearFilter: () => dispatch(clearFilter()),
    onExportCSV: (data: any) => dispatch(exportCSV(data)),
    onApproveOrder: (data) => dispatch(approveOrderActions.request(data)),
    onDisapproveOrder: (data) => dispatch(disapproveOrderActions.request(data)),
    onReOrderAll: (data) => dispatch(reOrderSubmit(data)),
    onBulkReOrder: (data) => dispatch(bulkReOrderSubmit(data)),
    onOpenModal: (data) => dispatch(openModal(data)),
    onCloseModal: () => dispatch(closeModal()),
    onSubmitFeedback: (data) => dispatch(submitFeedback(data)),
    onLoadMoreOrders: () => dispatch(loadMoreOrders()),
    onFetchTabCounts: () => dispatch(fetchTabCounts.request()),
    onApproveOrders: (orderIds: string[]) => dispatch(approveOrders.request(orderIds)),
    onDeclineOrders: (orderIds: string[]) => dispatch(declineOrders.request(orderIds)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

// <OwnProps> restricts access to the HOC's other props. This component must not do anything with reducer hoc
const withReducer = injectReducer<OwnProps>({
  key: 'ordersPage',
  reducer: reducer,
});
// <OwnProps> restricts access to the HOC's other props. This component must not do anything with saga hoc
const withSaga = injectSaga<OwnProps>({ key: 'ordersPage', saga: saga });

export default compose(withReducer, withSaga, withConnect)(OrdersPage);
